import { htmlToStructuredText } from 'datocms-html-to-structured-text';
import { type Document } from 'datocms-structured-text-utils';
import { clone, pipe, prop, replace } from 'ramda';

import {
  type CookieBannerText,
  type CookieCategory,
  type CookieCategoryKey,
  cookieCategoryKeys,
} from '@/app/components/server/cookie-banner/types';
import { toUpperPascalCase } from '@/lib/utils';
import {
  type CookieCategoryKeyCapitalized,
  type Cookiebot,
  type CookiebotDialog,
} from './types';

// Defining this function type makes functions below a little easier to read
type VoidFunction = () => void;

type CategoryCreatorFunction = (key: CookieCategoryKey) => CookieCategory;

const cookiebotEvents = [
  'CookiebotOnAccept',
  'CookiebotOnConsentReady',
  'CookiebotOnDecline',
  'CookiebotOnDialogDisplay',
  'CookiebotOnDialogInit',
  'CookiebotOnLoad',
  'CookiebotOnTagsExecuted',
] as const;

// Keep the most recent version of the Cookiebot object to send to the hook
// (otherwise we'll have an infinite loop if we keep making shallow copies)
let cookiebot: Cookiebot | null = null;

const updateCookiebotStore = (callback: VoidFunction): void => {
  if (
    typeof window === 'undefined' ||
    !window.Cookiebot ||
    // The Cookiebot script tag also has the HTML id `Cookiebot` which creates
    // an HTMLElement variable initially
    window.Cookiebot instanceof HTMLElement
  )
    return;

  // Create a copy so the hook can register a change
  cookiebot = clone(window.Cookiebot);
  callback();
};

const subscribe = (callback: VoidFunction): VoidFunction => {
  const updateStoreAndNotify = () => {
    updateCookiebotStore(callback);
  };

  // Update the store if it's still empty
  if (!cookiebot) updateStoreAndNotify();

  // Otherwise update it if one of the Cookiebot events fire
  cookiebotEvents.forEach((eventName) =>
    window.addEventListener(eventName, updateStoreAndNotify)
  );

  return () => {
    cookiebotEvents.forEach((eventName) =>
      window.removeEventListener(eventName, updateStoreAndNotify)
    );
  };
};

const getSnapshot = (): Cookiebot | null => cookiebot;

const getServerSnapshot = (): null => null;

// For use with useSyncExternalStore hook
export const cookiebotStore = { getServerSnapshot, getSnapshot, subscribe };

// Texts that include accentuated characters in Cookiebot come in HTML character
// codes so we need to convert them to the correct format before displaying.
// This is only for some of the strings, but doing it for all to be safe.
const sanitizeContent = (content: string) =>
  content.replace(/&#(\d+);/gu, (_, n: number) => String.fromCodePoint(n));

const capitalizeCategoryKey = (
  key: CookieCategoryKey
): CookieCategoryKeyCapitalized => {
  const irregularCategoryMap: Partial<
    Record<CookieCategoryKey, CookieCategoryKeyCapitalized>
  > = {
    marketing: 'Advertising',
    preferences: 'Preference',
  };

  return (
    irregularCategoryMap[key] ??
    (toUpperPascalCase(key) as CookieCategoryKeyCapitalized)
  );
};

export const getCategoryCookieAmount = (
  categoryKey: CookieCategoryKey,
  cookiebotDialog?: CookiebotDialog | null
): string => {
  if (!cookiebotDialog) return '…';

  const capitalizedKey = capitalizeCategoryKey(categoryKey);
  const categoryCountKey = `cookieTable${capitalizedKey}Count` as const;
  const amount = cookiebotDialog[categoryCountKey] ?? '';
  return String(amount);
};

const getCategoryCreator =
  (cookiebotDialog: CookiebotDialog): CategoryCreatorFunction =>
  (key) => {
    const capitalizedKey = capitalizeCategoryKey(key);
    const cookieIntro = `cookieIntroType${capitalizedKey}` as const;
    const cookieHeader = `cookieHeaderType${capitalizedKey}` as const;
    return {
      description: sanitizeContent(cookiebotDialog[cookieIntro]),
      key,
      name: pipe(
        prop(cookieHeader),
        sanitizeContent,
        replace('{0}', '{amount}')
      )(cookiebotDialog),
    };
  };

export const getTextFromCookiebot = async (
  cookiebotDialog: CookiebotDialog
): Promise<CookieBannerText> => {
  const categories = cookieCategoryKeys.map(
    getCategoryCreator(cookiebotDialog)
  );

  const descriptionValue: Document | null = await pipe(
    prop('text'),
    sanitizeContent,
    htmlToStructuredText
  )(cookiebotDialog);

  return {
    acceptAllButtonText: sanitizeContent(cookiebotDialog.acceptText),
    acceptSelectedButtonText: sanitizeContent(
      cookiebotDialog.loiAllowSelectionText
    ),
    categories,
    declineButtonText: sanitizeContent(cookiebotDialog.declineText),
    ...(descriptionValue && {
      description: { value: descriptionValue },
    }),
    hideDetailsText: sanitizeContent(cookiebotDialog.hideDetailsText),
    showDetailsText: sanitizeContent(cookiebotDialog.showDetailsText),
    title: sanitizeContent(cookiebotDialog.title),
  };
};
